import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ExternalLink, RefreshCcw } from 'lucide-react'
import { Badge } from '../../../components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card'
import { getLocalApiUrl, authFetch } from '../../../config/api'

const TAB_PREVIEW_LIMIT = 5

const COMPLIANCE_TABS = [
  { id: 'kyc', label: 'KYC' },
  { id: 'ecdd', label: 'ECDD' },
  { id: 'smr', label: 'SMR' },
  { id: 'uar', label: 'UAR' },
  { id: 'alerts', label: 'Alerts' },
  { id: 'investigations', label: 'Investigations' },
]

function normalizeCaseType(caseType) {
  return String(caseType || '').toUpperCase()
}

export function caseDetailHref(c) {
  const caseType = normalizeCaseType(c?.case_type)
  const id = c?.id
  if (!id) return null
  if (caseType === 'ARCHIVAL_SMR') {
    const ref = c?.case_number || id
    return `/compliance-safe/smr-registry?caseId=${encodeURIComponent(ref)}`
  }
  if (caseType === 'ECDD') return `/compliance-safe/ecdd/${encodeURIComponent(id)}`
  if (caseType === 'SMR' || caseType === 'SUSPICIOUS') {
    return `/compliance-safe/investigations/${encodeURIComponent(id)}?tab=smr_form`
  }
  if (caseType === 'INVESTIGATION') {
    return `/compliance-safe/investigations/${encodeURIComponent(id)}`
  }
  return null
}

function relatedCaseHref(relatedCaseId) {
  const id = String(relatedCaseId || '').trim()
  if (!id) return null
  return `/compliance-safe/investigations/${encodeURIComponent(id)}`
}

function formatDateTime(value) {
  if (!value) return '—'
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString()
}

function caseTypeBadgeClass(caseType) {
  const t = normalizeCaseType(caseType)
  if (t === 'SMR' || t === 'SUSPICIOUS' || t === 'ARCHIVAL_SMR') return 'bg-amber-100 text-amber-800 border-amber-200'
  if (t === 'ECDD') return 'bg-blue-100 text-blue-800 border-blue-200'
  if (t === 'INVESTIGATION') return 'bg-teal-100 text-teal-800 border-teal-200'
  return 'bg-gray-100 text-gray-800 border-gray-200'
}

const INCIDENT_TYPE_LABELS = {
  third_party: 'Third-party collection',
  structuring: 'Structuring / smurfing',
  minimal_play: 'Minimal play / cash conversion',
  suspicious: 'Suspicious behaviour',
  counterfeit_fraud: 'Counterfeit / fraud',
  self_exclusion: 'Self-exclusion breach',
  intoxication: 'Intoxication',
  underage: 'Underage',
  other: 'Other concern',
  no_incident: 'No incident to report',
}

function incidentTypeLabel(type) {
  const t = String(type || '').trim()
  if (!t) return 'Incident report'
  return INCIDENT_TYPE_LABELS[t] || t.replace(/_/g, ' ')
}

function incidentHref(inc) {
  const linked = inc?.linked_case_id || inc?.linkedCaseId
  if (linked && String(linked).trim()) {
    return `/compliance-safe/investigations/${encodeURIComponent(String(linked).trim())}`
  }
  const id = inc?.id
  if (id) return `/compliance-safe/incidents?highlight=${encodeURIComponent(String(id))}`
  return '/compliance-safe/incidents'
}

function alertHref(alert) {
  const id = alert?.id
  if (id == null) return null
  return `/monitor-safe/alerts/${encodeURIComponent(String(id))}`
}

function alertSeverityClass(severity) {
  const s = String(severity || '').toLowerCase()
  if (s === 'critical') return 'bg-red-100 text-red-800 border-red-200'
  if (s === 'high') return 'bg-orange-100 text-orange-800 border-orange-200'
  if (s === 'medium') return 'bg-amber-100 text-amber-800 border-amber-200'
  return 'bg-slate-100 text-slate-800 border-slate-200'
}

function kycReasonLabel(reason) {
  const r = String(reason || '').trim()
  if (!r) return 'KYC'
  return r.replace(/_/g, ' ')
}

/** Open KYC Registry filtered to a member, optionally with a specific record modal. */
export function kycRegistryHref(memberId, kycId) {
  const mid = String(memberId || '').trim()
  const params = new URLSearchParams()
  if (mid) params.set('member_id', mid)
  if (kycId != null && String(kycId).trim()) params.set('kyc_id', String(kycId).trim())
  const qs = params.toString()
  return qs ? `/compliance-safe/kyc-registry?${qs}` : '/compliance-safe/kyc-registry'
}

function sortNewestFirst(items, dateFn) {
  return [...(items || [])].sort(
    (a, b) => new Date(dateFn(b) || 0).getTime() - new Date(dateFn(a) || 0).getTime()
  )
}

function groupCases(caseHistory) {
  const groups = { ecdd: [], smr: [], investigation: [], other: [] }
  for (const c of caseHistory || []) {
    const t = normalizeCaseType(c.case_type)
    if (t === 'ECDD') groups.ecdd.push(c)
    else if (t === 'SMR' || t === 'SUSPICIOUS' || t === 'ARCHIVAL_SMR') groups.smr.push(c)
    else if (t === 'INVESTIGATION') groups.investigation.push(c)
    else groups.other.push(c)
  }
  return groups
}

function ComplianceRow({ badge, badgeClass, title, meta, href, date }) {
  return (
    <div className={`flex items-center justify-between border rounded-lg p-3 gap-3 ${href ? 'hover:bg-gray-50' : ''}`}>
      <div className="flex items-center gap-3 min-w-0">
        <Badge className={`shrink-0 ${badgeClass}`}>{badge}</Badge>
        {href ? (
          <Link to={href} className="font-medium text-sm text-primary hover:underline flex items-center gap-1 truncate">
            <span className="truncate">{title}</span>
            <ExternalLink className="h-3 w-3 shrink-0" />
          </Link>
        ) : (
          <span className="font-medium text-sm truncate">{title}</span>
        )}
        {meta && <span className="text-xs text-muted-foreground truncate">{meta}</span>}
      </div>
      <div className="text-right text-xs text-gray-500 shrink-0">{formatDateTime(date)}</div>
    </div>
  )
}

function ComplianceTabBar({ tabs, activeTab, onTabChange }) {
  return (
    <div className="overflow-x-auto -mx-1 px-1 pb-1">
      <div className="inline-flex min-w-full sm:min-w-0 rounded-md shadow-sm border border-psg-teal overflow-hidden bg-white">
        {tabs.map((tab, index) => {
          const isActive = activeTab === tab.id
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onTabChange(tab.id)}
              className={`shrink-0 px-3 py-2 text-sm font-medium flex items-center gap-1.5 transition-colors ${
                index > 0 ? 'border-l border-psg-teal' : ''
              } ${isActive ? 'bg-psg-teal text-white' : 'bg-white text-psg-teal hover:bg-psg-teal-50'}`}
            >
              <span>{tab.label}</span>
              <span
                className={`inline-flex items-center rounded-full px-1.5 text-xs font-semibold ${
                  isActive ? 'bg-white/20 text-white' : 'bg-psg-teal/10 text-psg-teal'
                }`}
              >
                {tab.count}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

function ComplianceTabPanel({
  title,
  count,
  viewAllHref,
  viewAllLabel,
  emptyText,
  isLoading,
  previewLimit = TAB_PREVIEW_LIMIT,
  expanded,
  onToggleExpanded,
  children,
}) {
  return (
    <div role="tabpanel" className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-gray-800">
          {title}
          {count > 0 ? ` (${count})` : ''}
        </h4>
        {count > 0 && viewAllHref && (
          <Link to={viewAllHref} className="text-xs text-primary font-medium hover:underline flex items-center gap-1 shrink-0">
            {viewAllLabel || 'View all'}
            <ExternalLink className="h-3 w-3" />
          </Link>
        )}
      </div>
      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
          <RefreshCcw className="h-4 w-4 animate-spin" />
          Loading…
        </div>
      ) : count === 0 ? (
        <p className="text-sm text-gray-500">{emptyText}</p>
      ) : (
        <>
          <div className={`space-y-2 ${expanded ? 'max-h-[28rem] overflow-y-auto pr-1' : ''}`}>{children}</div>
          {count > previewLimit && onToggleExpanded && (
            <button
              type="button"
              onClick={onToggleExpanded}
              className="text-sm font-medium text-primary hover:underline"
            >
              {expanded ? 'Show less' : `Show ${count - previewLimit} more`}
            </button>
          )}
        </>
      )}
    </div>
  )
}

export function MemberComplianceRecord({
  memberId,
  membershipNumber,
  caseHistory = [],
  kycEntries = [],
  incidents = [],
  monitorAlerts = [],
  amlCount = 0,
  isLoadingCases = false,
  isLoadingKyc = false,
  isLoadingIncidents = false,
  isLoadingAlerts = false,
}) {
  const registryMemberId = membershipNumber || memberId
  const kycRegistryListHref = kycRegistryHref(registryMemberId)

  const groups = useMemo(() => groupCases(caseHistory), [caseHistory])

  const activityIncidents = (incidents || []).filter(
    (inc) => String(inc?.incident_type || '').trim() !== 'no_incident'
  )

  const sortedLists = useMemo(
    () => ({
      kyc: sortNewestFirst(kycEntries, (k) => k.performed_at || k.created_at),
      ecdd: sortNewestFirst(groups.ecdd, (c) => c.created_at),
      smr: sortNewestFirst(groups.smr, (c) => c.austrac_filed_at || c.created_at),
      uar: sortNewestFirst(activityIncidents, (inc) => inc.created_at || inc.reported_at),
      alerts: sortNewestFirst(monitorAlerts, (a) => a.first_seen || a.last_updated || a.created_at),
      investigations: sortNewestFirst(groups.investigation, (c) => c.created_at),
    }),
    [kycEntries, groups, activityIncidents, monitorAlerts]
  )
  const ongoing = caseHistory.filter((c) =>
    ['OPEN', 'INVESTIGATING', 'PENDING_REVIEW', 'SUSPICION_FORMED', 'SMR_DRAFT', 'PENDING_APPROVAL'].includes(
      String(c.status || '').toUpperCase()
    )
  ).length

  const incidentsHref = registryMemberId
    ? `/compliance-safe/incidents?member_id=${encodeURIComponent(registryMemberId)}`
    : '/compliance-safe/incidents'
  const alertsHref = registryMemberId
    ? `/monitor-safe/alerts?q=${encodeURIComponent(registryMemberId)}`
    : '/monitor-safe/alerts'

  const tabCounts = useMemo(
    () => ({
      kyc: kycEntries.length,
      ecdd: groups.ecdd.length,
      smr: groups.smr.length,
      uar: activityIncidents.length,
      alerts: monitorAlerts.length,
      investigations: groups.investigation.length,
    }),
    [kycEntries.length, groups.ecdd.length, groups.smr.length, activityIncidents.length, monitorAlerts.length, groups.investigation.length]
  )

  const tabs = useMemo(
    () => COMPLIANCE_TABS.map((tab) => ({ ...tab, count: tabCounts[tab.id] ?? 0 })),
    [tabCounts]
  )

  const [activeTab, setActiveTab] = useState('kyc')
  const [tabInitialized, setTabInitialized] = useState(false)
  const [tabListExpanded, setTabListExpanded] = useState(false)

  const anyLoading = isLoadingCases || isLoadingKyc || isLoadingIncidents || isLoadingAlerts

  useEffect(() => {
    setTabListExpanded(false)
  }, [activeTab])

  function sliceForPreview(items) {
    const list = items || []
    if (tabListExpanded || list.length <= TAB_PREVIEW_LIMIT) return list
    return list.slice(0, TAB_PREVIEW_LIMIT)
  }

  useEffect(() => {
    if (tabInitialized || anyLoading) return
    const firstWithData = COMPLIANCE_TABS.find((tab) => (tabCounts[tab.id] ?? 0) > 0)
    setActiveTab(firstWithData?.id || 'kyc')
    setTabInitialized(true)
  }, [anyLoading, tabCounts, tabInitialized])

  const tabLoading = {
    kyc: isLoadingKyc,
    ecdd: isLoadingCases,
    smr: isLoadingCases,
    uar: isLoadingIncidents,
    alerts: isLoadingAlerts,
    investigations: isLoadingCases,
  }

  const tabPanelProps = {
    previewLimit: TAB_PREVIEW_LIMIT,
    expanded: tabListExpanded,
    onToggleExpanded: () => setTabListExpanded((v) => !v),
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Member compliance record</CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          {ongoing > 0 ? `${ongoing} ongoing case${ongoing === 1 ? '' : 's'}` : 'Linked KYC, ECDD, SMR, and investigations'}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <ComplianceTabBar
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={(id) => {
            setActiveTab(id)
            setTabInitialized(true)
          }}
        />

        {activeTab === 'kyc' && (
          <ComplianceTabPanel
            title="KYC performed"
            count={tabCounts.kyc}
            viewAllHref={kycRegistryListHref}
            viewAllLabel="KYC Registry"
            emptyText="No KYC registry entries for this member."
            isLoading={tabLoading.kyc}
            {...tabPanelProps}
          >
            {sliceForPreview(sortedLists.kyc).map((k) => {
              const relatedHref = relatedCaseHref(k.related_case_id)
              const recordHref = kycRegistryHref(registryMemberId || k.member_id, k.id)
              const title = `KYC #${k.id} — ${kycReasonLabel(k.reason)}`
              const meta = k.payout_amount
                ? `$${Number(k.payout_amount).toLocaleString()}`
                : k.related_case_id
                  ? `Case ${k.related_case_id}`
                  : null
              return (
                <ComplianceRow
                  key={`kyc-${k.id}`}
                  badge="KYC"
                  badgeClass="bg-violet-100 text-violet-800 border-violet-200"
                  title={title}
                  meta={relatedHref ? null : meta}
                  href={relatedHref || recordHref}
                  date={k.performed_at || k.created_at}
                />
              )
            })}
          </ComplianceTabPanel>
        )}

        {activeTab === 'ecdd' && (
          <ComplianceTabPanel
            title="ECDD"
            count={tabCounts.ecdd}
            emptyText="No ECDD cases on record."
            isLoading={tabLoading.ecdd}
            {...tabPanelProps}
          >
            {sliceForPreview(sortedLists.ecdd).map((c) => (
              <ComplianceRow
                key={`ecdd-${c.id}`}
                badge="ECDD"
                badgeClass={caseTypeBadgeClass(c.case_type)}
                title={c.case_number || c.id}
                meta={c.status}
                href={caseDetailHref(c)}
                date={c.created_at}
              />
            ))}
          </ComplianceTabPanel>
        )}

        {activeTab === 'smr' && (
          <ComplianceTabPanel
            title="SMR"
            count={tabCounts.smr}
            emptyText="No SMR cases on record."
            isLoading={tabLoading.smr}
            {...tabPanelProps}
          >
            {sliceForPreview(sortedLists.smr).map((c) => {
              const archival = normalizeCaseType(c.case_type) === 'ARCHIVAL_SMR'
              return (
                <ComplianceRow
                  key={`smr-${c.id}`}
                  badge={archival ? 'Historical' : 'SMR'}
                  badgeClass={caseTypeBadgeClass(c.case_type)}
                  title={c.case_number || c.id}
                  meta={c.austrac_reference ? `Ref ${c.austrac_reference}` : c.status}
                  href={caseDetailHref(c)}
                  date={c.austrac_filed_at || c.created_at}
                />
              )
            })}
          </ComplianceTabPanel>
        )}

        {activeTab === 'uar' && (
          <ComplianceTabPanel
            title="UAR / incident reports"
            count={tabCounts.uar}
            viewAllHref={incidentsHref}
            viewAllLabel="Incident monitoring"
            emptyText="No UAR or incident reports linked to this member."
            isLoading={tabLoading.uar}
            {...tabPanelProps}
          >
            {sliceForPreview(sortedLists.uar).map((inc) => {
              const type = String(inc.incident_type || '').trim()
              const title = `${inc.id || 'Report'} — ${incidentTypeLabel(type)}`
              const meta = inc.severity
                ? `${String(inc.severity).toUpperCase()} · ${inc.status || 'open'}`
                : inc.status || null
              return (
                <ComplianceRow
                  key={`inc-${inc.id}`}
                  badge="UAR"
                  badgeClass="bg-yellow-100 text-yellow-800 border-yellow-200"
                  title={title}
                  meta={meta}
                  href={incidentHref(inc)}
                  date={inc.created_at || inc.reported_at}
                />
              )
            })}
          </ComplianceTabPanel>
        )}

        {activeTab === 'alerts' && (
          <ComplianceTabPanel
            title="Monitor alerts"
            count={tabCounts.alerts}
            viewAllHref={alertsHref}
            viewAllLabel="Alert queue"
            emptyText="No rule alerts for this member."
            isLoading={tabLoading.alerts}
            {...tabPanelProps}
          >
            {sliceForPreview(sortedLists.alerts).map((a) => (
              <ComplianceRow
                key={`alert-${a.id}`}
                badge="Alert"
                badgeClass={alertSeverityClass(a.severity)}
                title={a.rule_name || `Alert #${a.id}`}
                meta={a.status || a.category || null}
                href={alertHref(a)}
                date={a.first_seen || a.last_updated || a.created_at}
              />
            ))}
          </ComplianceTabPanel>
        )}

        {activeTab === 'investigations' && (
          <ComplianceTabPanel
            title="Investigations"
            count={tabCounts.investigations}
            emptyText="No investigation cases on record."
            isLoading={tabLoading.investigations}
            {...tabPanelProps}
          >
            {sliceForPreview(sortedLists.investigations).map((c) => (
              <ComplianceRow
                key={`inv-${c.id}`}
                badge="Investigation"
                badgeClass={caseTypeBadgeClass(c.case_type)}
                title={c.case_number || c.id}
                meta={c.status}
                href={caseDetailHref(c)}
                date={c.created_at}
              />
            ))}
          </ComplianceTabPanel>
        )}

        <div className="border-t pt-4 flex flex-wrap gap-3 text-sm">
          <span className="text-gray-600">
            AML/PEP checks on this page: <strong>{amlCount}</strong>
          </span>
          {registryMemberId && (
            <Link to={kycRegistryListHref} className="text-primary font-medium hover:underline flex items-center gap-1">
              Open KYC Registry (filtered)
              <ExternalLink className="h-3 w-3" />
            </Link>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

/** Load KYC registry rows for a member (club # or player id). */
export async function fetchKycEntriesForMember(memberKeys) {
  const keys = [...new Set((memberKeys || []).map((k) => String(k).trim()).filter(Boolean))]
  const merged = []
  const seen = new Set()

  for (const key of keys) {
    try {
      const qs = new URLSearchParams({ member_id: key, limit: '50' })
      const res = await authFetch(getLocalApiUrl(`/api/kyc-registry?${qs}`))
      const json = await res.json()
      if (!json.success || !Array.isArray(json.data)) continue
      for (const row of json.data) {
        const id = row.id
        if (id != null && !seen.has(id)) {
          seen.add(id)
          merged.push(row)
        }
      }
    } catch (_) {
      /* ignore per-key failures */
    }
  }

  return merged.sort((a, b) => {
    const ta = new Date(a.performed_at || a.created_at || 0).getTime()
    const tb = new Date(b.performed_at || b.created_at || 0).getTime()
    return tb - ta
  })
}

/** UAR / incident reports (same table as mobile floor UAR). */
export async function fetchIncidentsForMember(memberId) {
  const mid = String(memberId || '').trim()
  if (!mid) return []
  try {
    const res = await authFetch(
      getLocalApiUrl(`/api/mobile/incidents?member_id=${encodeURIComponent(mid)}&limit=50`)
    )
    const json = await res.json()
    if (!res.ok || !json.success) return []
    return Array.isArray(json.data) ? json.data : []
  } catch (_) {
    return []
  }
}

/** Monitor-safe rule alerts for this member. */
export async function fetchMonitorAlertsForMember(memberId) {
  const mid = String(memberId || '').trim()
  if (!mid) return []
  try {
    const res = await authFetch(
      getLocalApiUrl(`/api/alerts/list?member_id=${encodeURIComponent(mid)}&limit=50`)
    )
    const json = await res.json()
    if (!res.ok || !json.success) return []
    const data = json.data
    return Array.isArray(data) ? data : []
  } catch (_) {
    return []
  }
}
